import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import UserModal from './modals/UserModal';

const Users = ({ apiCall, showSuccess, showError }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);  // ADD THIS
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load roles first
      const rolesData = await apiCall('/roles');
      console.log('Roles loaded:', rolesData);
      setRoles(rolesData || []);
      
      // Load users
      const usersData = await apiCall('/users');
      console.log('Users loaded:', usersData);
      setUsers(usersData || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Error loading users: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await apiCall(`/users/${userId}`, { method: 'DELETE' });
      showSuccess('User deleted successfully');
      loadData();
    } catch (error) {
      showError(error.message);
    }
  };

  const handleUserSuccess = () => {
    setShowModal(false);
    setEditingUser(null);
    loadData();
    showSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">System Users</h2>
          <button 
            onClick={() => { setEditingUser(null); setShowModal(true); }} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            <Plus size={18} />
            New User
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Username</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Full Name</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Roles</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No users found. Click "New User" to create one.
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.UserID} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">{user.Username}</td>
                    <td className="px-4 py-3">{user.FullName}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {user.Roles?.split(',').map((role, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.IsActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit user"
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={() => handleDelete(user.UserID)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete user"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <UserModal
          apiCall={apiCall}
          roles={roles}  // PASS ROLES HERE
          editingUser={editingUser}
          onClose={() => { setShowModal(false); setEditingUser(null); }}
          onSuccess={handleUserSuccess}
          showError={showError}
        />
      )}
    </div>
  );
};

export default Users;